import type {
    DataTableColumn,
    OptionItem,
} from '@/components/ui/data-table-customized'
import {
    createDateFormatter,
    createInternalLinkFormatter,
    createOptionsFormatter,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import type { AddonConfigOption } from '@/modules/Customer/types'
import {
    createConfigOptionsColumns,
    createConfigOptionsColumnsByTemplate,
    createExpandedRowColumnsByTemplate,
    createExpandedRowData,
    createExpandedRowRender,
    isAddonActiveForSummary,
    shouldDisplayValue,
} from '@/modules/Service/utils/expanded-row'
import {
    createSmartExpandedRow,
    getConfigQuota,
    getTemplateConfig,
    getTemplateFieldNames,
    isTemplate,
} from '@/modules/Service/utils/templates'
import { formatNumberUsage } from '@/utils/function'
import { TFunction } from 'i18next'
import type { ReactNode } from 'react'

// Re-export from Service module for backward compatibility
export {
    createConfigOptionsColumns,
    createConfigOptionsColumnsByTemplate,
    createExpandedRowColumnsByTemplate,
    createExpandedRowData,
    createExpandedRowRender,
    // Template registry (new centralized approach)
    createSmartExpandedRow,
    getConfigQuota,
    getTemplateConfig,
    isTemplate,
    shouldDisplayValue,
}

// ============================================================================
// Config Summary Helpers
// ============================================================================

interface ConfigSummaryItem {
    name: string
    value: number
    unit: string
}

/**
 * Aggregate configoptions from main service + addons by matching name
 * Returns summarized array with combined quota values
 * Only includes configs that are in the template field names (filters out unwanted configs)
 * Excludes addons with status: pending-terminated, rejected, cancelled
 */
const summarizeConfigOptions = (row: any): ConfigSummaryItem[] => {
    const summaryMap = new Map<string, ConfigSummaryItem>()
    const serviceTemplate = row?.service_template
    const fieldNames = getTemplateFieldNames(serviceTemplate)

    // Helper to process config and add to summary
    const processConfig = (config: any, template?: string) => {
        const unit = config.unit || config.capacity_unit || ''
        const key = config.name?.toLowerCase()
        if (!key) return

        // Only include configs that are in the field names list
        if (!fieldNames.includes(key)) return

        if (!summaryMap.has(key)) {
            summaryMap.set(key, {
                name: config.name,
                value: 0,
                unit,
            })
        }
        const item = summaryMap.get(key)!
        item.value += getConfigQuota(config, template)
    }

    // Add main service configs
    const mainConfigs = row?.configoptions || []
    mainConfigs.forEach((config: any) => processConfig(config, serviceTemplate))

    // Add addon configs (exclude inactive addons)
    const addons = (row?.addons || []).filter(isAddonActiveForSummary)
    addons.forEach((addon: any) => {
        const addonTemplate = addon?.addon_service_template || serviceTemplate
        const addonConfigs = addon?.configoptions || []
        addonConfigs.forEach((config: any) =>
            processConfig(config, addonTemplate)
        )
    })

    // Sort by template field order
    const configs = Array.from(summaryMap.values())
    configs.sort((a, b) => {
        const aIndex = fieldNames.indexOf(a.name.toLowerCase())
        const bIndex = fieldNames.indexOf(b.name.toLowerCase())
        return aIndex - bIndex
    })

    return configs
}

// ============================================================================
// Price Calculation Helpers
// ============================================================================

interface PriceSummary {
    register: number
    recurring: number
}

/**
 * Calculate price from configoptions for bundle pricing model
 * Sum of: commitment_value * commitment_price_per_unit
 */
const calculateBundlePrice = (configoptions: any[]): PriceSummary => {
    const register = 0
    let recurring = 0

    configoptions?.forEach((config: any) => {
        const commitment = config.commitment_value || 1
        const pricePerUnit = config.commitment_price_per_unit || 0
        const configPrice = commitment * pricePerUnit
        // Assuming bundle configs contribute to recurring price
        recurring += configPrice
    })

    return { register, recurring }
}

/**
 * Calculate total price for service (main + addons)
 * - package: use service_price_register/recurring directly
 * - bundle: sum commitment_value * commitment_price_per_unit from configoptions
 */
const calculateTotalPrice = (row: any): PriceSummary => {
    let totalRegister = 0
    let totalRecurring = 0

    // Main service price
    const mainPricingModel = row?.service_pricing_model?.toLowerCase()
    if (mainPricingModel === 'bundle') {
        const bundlePrice = calculateBundlePrice(row?.configoptions)
        totalRegister += bundlePrice.register
        totalRecurring += bundlePrice.recurring
    } else {
        // package or other models - use direct price
        totalRegister += row?.service_price_register || 0
        totalRecurring += row?.service_price_recurring || 0
    }

    // Addon prices
    const addons = row?.addons || []
    addons.forEach((addon: any) => {
        const addonPricingModel = addon?.addon_pricing_model?.toLowerCase()
        if (addonPricingModel === 'bundle') {
            const bundlePrice = calculateBundlePrice(addon?.configoptions)
            totalRegister += bundlePrice.register
            totalRecurring += bundlePrice.recurring
        } else {
            // package or other models
            totalRegister += addon?.addon_price_register || 0
            totalRecurring += addon?.addon_price_recurring || 0
        }
    })

    return { register: totalRegister, recurring: totalRecurring }
}

/**
 * Render config options as Chips
 */
const renderConfigChips = (row: any, t: TFunction): ReactNode => {
    const configs = summarizeConfigOptions(row)
    // Filter out configs with 0, empty, or falsy values using shared utility
    const validConfigs = configs.filter((c) => shouldDisplayValue(c.value))
    if (validConfigs.length === 0) return '-'

    return (
        <div className="flex items-center gap-1 overflow-hidden">
            {validConfigs.map((config) => {
                // Hide 'qty' unit in display
                const displayUnit =
                    config.unit?.toLowerCase() === 'qty'
                        ? ''
                        : config.unit?.toUpperCase() || ''
                return (
                    <span
                        key={config.name}
                        className="inline-flex items-center rounded-full border bg-gray-100 text-gray-800 border-gray-200 px-2 py-0.5 text-xs whitespace-nowrap"
                    >
                        {t(config.name, { defaultValue: config.name })}:{' '}
                        {formatNumberUsage(config.value)}
                        {displayUnit ? ` ${displayUnit}` : ''}
                    </span>
                )
            })}
        </div>
    )
}

// ============================================================================
// Service Status Options
// ============================================================================

const SERVICE_STATUS_OPTIONS = (t: TFunction): OptionItem[] => [
    {
        label: t('service-status.waiting-confirm'),
        value: 'waiting-confirm',
        color: 'info',
    },
    {
        label: t('service-status.waiting-create-service'),
        value: 'waiting-create-service',
        color: 'warning',
    },
    {
        label: t('service-status.trialing'),
        value: 'trialing',
        color: 'primary',
    },
    {
        label: t('service-status.active'),
        value: 'active',
        color: 'success',
    },
    {
        label: t('service-status.on-resubmit-information'),
        value: 'on-resubmit-information',
        color: 'warning',
    },
    {
        label: t('service-status.pending-terminated'),
        value: 'pending-terminated',
        color: 'error',
    },
    {
        label: t('service-status.cancelled'),
        value: 'cancelled',
        color: 'error',
    },
    {
        label: t('service-status.rejected'),
        value: 'rejected',
        color: 'error',
    },
]

// ============================================================================
// Column Configuration
// ============================================================================

export const createServicesColumns = (
    t: TFunction,
    _isPoc: boolean
): DataTableColumn<any>[] => {
    const columns: DataTableColumn<any>[] = [
        {
            key: 'service_id',
            title: t('service_id'),
            width: 350,
            sortable: true,
            pinned: 'left',
            cellClassName: 'uppercase truncate',
            formatter: createInternalLinkFormatter({
                pathKey: 'serviceDetail',
                pathParams: (value) => ({ id: value }),
                label: (value, row) =>
                    [value, row?.service_name].filter(Boolean).join(' - ') ||
                    value,
            }),
        },
        {
            key: 'product_group_name',
            title: t('product_group'),
            width: 230,
            sortable: true,
        },
        {
            key: 'service_entity_name',
            title: t('entity_name'),
            width: 200,
        },
        {
            key: 'configoptions',
            title: t('configuration', { defaultValue: 'Configuration' }),
            width: 280,
            render: (_, row) => renderConfigChips(row, t),
        },
        {
            key: 'service_status',
            title: t('service_status'),
            width: 150,
            sortable: true,
            formatter: createOptionsFormatter({
                options: SERVICE_STATUS_OPTIONS(t),
            }),
        },
        {
            key: 'service_price_register',
            title: t('service_price_register'),
            width: 150,
            cellClassName: 'text-right',
            render: (_, row) => {
                const { register } = calculateTotalPrice(row)
                return new Intl.NumberFormat('vi-VN', {
                    style: 'currency',
                    currency: 'VND',
                }).format(register)
            },
        },
        {
            key: 'service_price_recurring',
            title: t('service_price_recurring'),
            width: 150,
            cellClassName: 'text-right',
            render: (_, row) => {
                const { recurring } = calculateTotalPrice(row)
                return new Intl.NumberFormat('vi-VN', {
                    style: 'currency',
                    currency: 'VND',
                }).format(recurring)
            },
        },
        {
            key: 'service_start_date',
            title: t('service_start_date'),
            width: 100,
            cellClassName: 'text-right',
            formatter: createDateFormatter({
                format: 'DD/MM/YYYY',
            }),
        },
        {
            key: 'service_stopped_date',
            title: t('service_stopped_date'),
            width: 100,
            cellClassName: 'text-right',
            formatter: createDateFormatter({
                format: 'DD/MM/YYYY',
            }),
        },
    ]

    return columns
}

// ============================================================================
// Addon Configuration Columns
// ============================================================================

export const createAddonConfigColumns = (
    t: TFunction
): DataTableColumn<AddonConfigOption>[] => [
    {
        key: 'addon_service_name',
        title: t('addon_service', { defaultValue: 'Addon Service' }),
        width: 200,
        cellClassName: 'font-medium',
    },
    {
        key: 'name',
        title: t('config_name', { defaultValue: 'Configuration' }),
        width: 150,
        render: (value) => t(value, { defaultValue: value }),
    },
    {
        key: 'quota',
        title: t('quota', { defaultValue: 'Quota' }),
        width: 100,
        render: (_, row) => `${row.quota || 0} ${row.unit || ''}`,
    },
    {
        key: 'addon_service_status',
        title: t('status', { defaultValue: 'Status' }),
        width: 120,
        formatter: createOptionsFormatter({
            options: SERVICE_STATUS_OPTIONS(t),
        }),
    },
    {
        key: 'addon_price_recurring',
        title: t('price_recurring', { defaultValue: 'Recurring Price' }),
        width: 150,
        render: (value) =>
            new Intl.NumberFormat('vi-VN', {
                style: 'currency',
                currency: 'VND',
            }).format(value || 0),
    },
]

// ============================================================================
// Filter Configuration
// ============================================================================

interface ProductGroupOption {
    label: string
    value: string
}

export const createServicesFilters = (
    t: TFunction,
    productGroupOptions: ProductGroupOption[] = [],
    isPoc: boolean = false
): FilterItemConfig[] => {
    // Official services shouldn't have "trialing" status
    const statusOptions = isPoc
        ? SERVICE_STATUS_OPTIONS(t)
        : SERVICE_STATUS_OPTIONS(t).filter((opt) => opt.value !== 'trialing')

    return [
        {
            type: 'multi-select',
            name: 'service_status',
            label: t('service_status', { defaultValue: 'Status' }),
            options: statusOptions,
            placeholder: t('select-status', { defaultValue: 'Select status' }),
        },
        {
            type: 'multi-select',
            name: 'product_group_name',
            label: t('product_group', { defaultValue: 'Product Group' }),
            options: productGroupOptions,
            placeholder: t('select-product-group', {
                defaultValue: 'Select product group',
            }),
        },
    ]
}
